import { css } from '@emotion/react';
import styled from '@emotion/styled';
import dayjs, { locale } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { IMdResident } from '@/types/api/medical/getResidents';
import { color } from '@/styles';
import { OutlineButton } from '@/components/outlineButton';
import { Button } from '@/components/mui/button';
import { POSTURES } from '@/constants/activityStatus';
import api from '@/api';
import { IGetMdNursingHomePostureRes } from '@/types/api/medical/getNursingHomePosture';
import { batteryStatusStr } from '@/utils/batteryStatus';

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(duration);

interface Props {
  resident: IMdResident;
}

const HeaderContainer = css`
  width: 100%;
  min-height: 48px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 0;
  background-color: ${color.white};
  border-radius: 8px 8px 0 0;
  position: relative;
  z-index: 1;
`;

const ResidentName = css`
  display: flex;
  gap: 8px;
  font-weight: bold;
  font-size: 18px;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const ResidentInfo = css`
  font-size: 12px;
  color: ${color.gray};
  word-break: break-word;
  overflow-wrap: break-word;
`;

const HeaderLeftContainer = css`
  display: flex;
  justify-content: center;
  flex-direction: column;
  min-height: 48px;
  margin-right: 24px;
`;

const HeaderMiddleContainer = css`
  display: flex;
  column-gap: 7px;
`;

const HeaderRightContainer = css`
  display: flex;
  flex-grow: 1;
  flex-wrap: wrap-reverse;
  align-items: center;
  justify-content: end;
  min-height: 48px;
  gap: 16px;
`;

const HeaderRightButtonContainer = css`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DeviceInfoContainer = css`
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 25px 62px;
  align-items: center;
  align-content: center;
  column-gap: 8px;
`;

const SerialNumber = (textColor: string) => css`
  font-size: 14px;
  color: ${textColor};
  display: flex;
  align-items: center;
  column-gap: 6px;
`;

const NotificationSettingIcons = css`
  display: flex;
  align-items: center;
  column-gap: 5px;

  > img {
    width: 17px;
    height: auto;
  }
`;

const RecordButton = styled(Button)`
  border-radius: 24px;
  padding: 8px 24px;
`;

const PostureContainer = css`
  min-width: 90px;
  display: flex;
  align-items: center;
  column-gap: 10px;
`;

const PostureIcon = (isDisplay: boolean) => css`
  display: ${isDisplay ? 'block' : 'none'};
`;

const PostureText = (size: number, weight?: string) => css`
  font-size: ${` ${size}px`};
  font-weight: ${weight ?? 'normal'};
  line-height: 1;
`;

const PostureTextContainer = css`
  display: flex;
  flex-direction: column;
  row-gap: 6px;
  font-size: 12px;
`;

const DfreeGraphListItemHeader = ({ resident }: Props) => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  locale(i18n.language);

  const isDeviceConnected = React.useMemo(() => resident.isDeviceActive, [resident.isDeviceActive]);

  const handleGoDetail = React.useCallback(() => {
    history.push(`./residents/${resident.id}/graph`);
  }, []);

  const handleGoEdit = React.useCallback(() => {
    history.push(`./residents/${resident.id}/edit`);
  }, []);

  return (
    <div css={HeaderContainer}>
      <div css={HeaderLeftContainer}>
        <div css={ResidentName}>{resident.name}</div>
        <div css={ResidentInfo}>
          {resident.nursingHomeUnitName} {resident.roomName}
        </div>
      </div>
      <div css={HeaderMiddleContainer}>
        <Posture
          residentId={resident.id}
          posture={resident.posture}
          postureDuration={resident.postureDuration}
          isConnected={isDeviceConnected}
        />
      </div>
      <div css={HeaderRightContainer}>
        <DeviceInfo
          serialNumber={resident.serialNumber}
          batteryRemaining={resident.batteryRemaining}
          isDeviceConnected={isDeviceConnected}
          bigBladderNotificationSetting={resident.bigBladderNotificationSetting}
          urinatedNotificationSetting={resident.urinatedNotificationSetting}
          gotUpNotificationSettging={resident.gotUpNotificationSettging}
        />
        <div css={HeaderRightButtonContainer}>
          <OutlineButton
            onClick={handleGoDetail}
            icon={<img src='/images/icons/graph.svg' alt={t('history', '履歴')} />}
            bgColor={color.white}
          >
            {t('history', '履歴')}
          </OutlineButton>
          <OutlineButton
            onClick={handleGoEdit}
            icon={<img src='/images/icons/resident-setting.svg' alt='入居者設定' />}
            bgColor={color.white}
          >
            {t('edit', '編集')}
          </OutlineButton>
          {/* medicalでは尿量リセットに関わらない記録問題が解消したら記録ボタンを表示する 
          <RecordButton variant='contained' onClick={handleClickRecord}>
            {t('recordButton', '記録')}
          </RecordButton>
          */}
        </div>
      </div>
    </div>
  );
};

interface PostureProps {
  residentId: IMdResident['id'];
  posture: IMdResident['posture'];
  postureDuration: IMdResident['postureDuration'];
  isConnected: boolean;
}

type CurrentPosture = {
  posture: number;
  timestamp: string;
};

const Posture = ({ residentId, posture, postureDuration, isConnected }: PostureProps) => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { t, i18n } = useTranslation();
  locale(i18n.language);
  // 表示用の変数
  const [currentPosture, setCurrentPosture] = React.useState<CurrentPosture | null>(null);
  // 最新姿勢APIからデータを格納
  const [latestPosture, setLatestPosture] = React.useState<CurrentPosture | null>(null);
  // TOP用APIからのデータを格納
  const [topPosture, setTopPosture] = React.useState<CurrentPosture | null>(null);

  const [postureDurationText, setPostureDurationText] = React.useState<string>('');

  const isChangedPosture = (pos1: number, pos2: number) => {
    // 0→1,1→0の変化を姿勢変化とみなさない
    if ((pos1 === 1 && pos2 === 0) || (pos1 === 0 && pos2 === 1)) {
      return false;
    }
    return pos1 !== pos2;
  };

  const fetchPosture = React.useCallback(async () => {
    const result: IGetMdNursingHomePostureRes[] = await api.get(
      '/md/nursing-homes/:nursingHomeId/dfree-devices/stats/posture',
      {
        params: { nursingHomeId, residentId },
      },
    );

    // データが存在しているか
    // result[0]?.postureやlatestPosture?.postureは0の場合もあるので、!!での判定はしない
    if (!!result && typeof result[0]?.posture === 'number' && !!result[0]?.timestamp) {
      if (!latestPosture) {
        setLatestPosture({ posture: result[0].posture, timestamp: dayjs.utc().format('YYYY-MM-DDTHH:mm:ss[Z]') });
        return;
      }
      // 直前の値と異なる場合
      if (typeof latestPosture?.posture === 'number' && isChangedPosture(result[0].posture, latestPosture?.posture)) {
        setLatestPosture({ posture: result[0].posture, timestamp: result[0]?.timestamp });
        return;
      }
    }
  }, [nursingHomeId, residentId, latestPosture, isChangedPosture]);

  // 表示する経過時間を更新
  const updatePostureDurationText = React.useCallback(() => {
    const postureDurationDiffMs = dayjs().utc().diff(dayjs.utc(currentPosture?.timestamp), 'milliseconds');

    if (postureDurationDiffMs >= 12 * 60 * 60 * 1000) {
      setPostureDurationText(`${t('over12hr', '12時間以上')} ${t('elapsed', '経過')}`);
      return;
    }
    const duration = dayjs.duration(postureDurationDiffMs);
    setPostureDurationText(
      `${duration.hours()}${t('Hr', '時間')}${duration.minutes()}${t('Min', '分')} ${t('elapsed', '経過')}`,
    );
  }, [currentPosture]);

  // 5秒間隔で最新姿勢を取得する
  React.useEffect(() => {
    updatePostureDurationText();
    const timer = setInterval(() => {
      if (isConnected) {
        fetchPosture();
        updatePostureDurationText();
      }
    }, 5000);
    return () => {
      clearInterval(timer);
    };
  }, [updatePostureDurationText, fetchPosture, isConnected]);

  // 切断されたら状態を初期化する
  React.useEffect(() => {
    if (!isConnected) {
      setCurrentPosture(null);
      setLatestPosture(null);
      setTopPosture(null);
      updatePostureDurationText();
    }
  }, [isConnected]);

  // APIからのデータを見て最新状態に更新する
  React.useEffect(() => {
    // TOP用APIからのデータをチェック
    if (posture !== null && postureDuration !== null) {
      //  TOP用APIからの姿勢情報が未設定
      if (!topPosture || typeof topPosture?.posture !== 'number' || !topPosture.timestamp) {
        setTopPosture({ posture: posture, timestamp: postureDuration });
      }

      // 姿勢情報が未設定
      if (!currentPosture || typeof currentPosture.posture !== 'number' || !currentPosture.timestamp) {
        setCurrentPosture({ posture: posture, timestamp: postureDuration });
        return;
      }

      // 姿勢が変わったとき、姿勢情報を更新（TOP用APIからのデータ正とする）
      // 前回TOP用APIで取得した値と異なり、かつ表示中のものとも異なる場合
      // 表示中のものと比較するとき、姿勢は同じだけど経過時間が異なる場合もデータ更新
      if (
        typeof topPosture?.posture === 'number' &&
        isChangedPosture(posture, topPosture?.posture) &&
        (isChangedPosture(posture, currentPosture.posture) || postureDuration !== currentPosture.timestamp)
      ) {
        setCurrentPosture({ posture: posture, timestamp: postureDuration });
        setTopPosture({ posture: posture, timestamp: postureDuration });
        return;
      }
    }

    // 最新姿勢APIからのデータをチェック：latestPosture.timestampは取得ごとに変化するので、postureが変わったかのみチェック
    if (!!latestPosture && latestPosture?.posture !== null && latestPosture?.timestamp !== null) {
      // 姿勢情報が未設定
      if (!currentPosture || typeof currentPosture.posture !== 'number' || !currentPosture.timestamp) {
        setCurrentPosture({ posture: latestPosture.posture, timestamp: latestPosture.timestamp });
        return;
      }

      // 姿勢が変わったとき、姿勢情報を更新
      if (isChangedPosture(latestPosture.posture, currentPosture.posture)) {
        setCurrentPosture({ posture: latestPosture.posture, timestamp: latestPosture.timestamp });
        return;
      }
    }
  }, [posture, postureDuration, latestPosture?.posture]);

  const isDisplayPosture: boolean = React.useMemo(() => {
    return isConnected && !!currentPosture;
  }, [isConnected, currentPosture]);

  // 医療サービスではU2Pと同様の姿勢テーブルを使用
  const posturesInfo = POSTURES['u2p'];

  const postureInfo = React.useMemo(() => {
    if (isDisplayPosture) {
      return posturesInfo?.find((v) => v.id === currentPosture?.posture);
    }
  }, [currentPosture?.posture]);

  return (
    <div css={PostureContainer} data-testid='posture'>
      {isDisplayPosture ? (
        <>
          {posturesInfo?.map((v) => (
            <img key={v.id} src={v.iconPath} alt={t(v.nameKey, v.name)} css={PostureIcon(v.id === postureInfo?.id)} />
          ))}
          <img src='/images/residents/posture/none.svg' alt='-' css={PostureIcon(!postureInfo)} />
          <div css={PostureTextContainer}>
            {postureInfo ? (
              <>
                <span css={PostureText(12)}>{t(postureInfo?.nameKey, postureInfo?.name)}</span>
                <span css={PostureText(14)}>{currentPosture?.timestamp && postureDurationText}</span>
              </>
            ) : (
              <span css={PostureText(12, 'bold')}>-</span>
            )}
          </div>
        </>
      ) : (
        <>
          <img src='/images/residents/posture/none.svg' alt='-' />
          <span css={PostureText(12)}>-</span>
        </>
      )}
    </div>
  );
};

interface DeviceInfoProps {
  serialNumber: IMdResident['serialNumber'];
  batteryRemaining: IMdResident['batteryRemaining'];
  isDeviceConnected: boolean;
  bigBladderNotificationSetting: IMdResident['bigBladderNotificationSetting'];
  urinatedNotificationSetting: IMdResident['urinatedNotificationSetting'];
  gotUpNotificationSettging: IMdResident['gotUpNotificationSettging'];
}

const DeviceInfo = React.memo(
  ({
    serialNumber,
    batteryRemaining,
    isDeviceConnected,
    bigBladderNotificationSetting,
    urinatedNotificationSetting,
    gotUpNotificationSettging,
  }: DeviceInfoProps) => {
    const { t, i18n } = useTranslation();
    locale(i18n.language);

    const [deviceBatteryStatus, setDeviceBatteryStatus] = React.useState<string>(
      batteryStatusStr(batteryRemaining, isDeviceConnected),
    );

    React.useEffect(() => {
      setDeviceBatteryStatus(batteryStatusStr(batteryRemaining, isDeviceConnected));
    }, [batteryRemaining, isDeviceConnected]);

    return (
      <div css={DeviceInfoContainer} data-testid='device-info'>
        {serialNumber !== null ? (
          <>
            {deviceBatteryStatus === '3' ? (
              <div css={SerialNumber(color.red)}>
                <img src={'/images/residents/valid-device.svg'} alt='DFreeデバイス' />
                {t('charge', '要充電')}
              </div>
            ) : (
              <div css={SerialNumber(color.dark_gray)}>
                <img src={'/images/residents/valid-device.svg'} alt='DFreeデバイス' />
                {serialNumber}
              </div>
            )}
            <div>
              <img src={`/images/residents/battery-${deviceBatteryStatus}.svg`} alt='DFreeデバイスバッテリー残量' />
            </div>
            <div css={NotificationSettingIcons}>
              <img
                src={
                  '/images/residents/' + (bigBladderNotificationSetting ? 'on-bigbradder.svg' : 'off-bigbradder.svg')
                }
                alt='そろそろ通知'
              />
              <img
                src={'/images/residents/' + (urinatedNotificationSetting ? 'on-urination.svg' : 'off-urination.svg')}
                alt='出たかも通知'
              />
              <img
                src={'/images/residents/' + (gotUpNotificationSettging ? 'on-gotup.svg' : 'off-gotup.svg')}
                alt='起き上がり'
              />
            </div>
          </>
        ) : (
          <>
            <div css={SerialNumber(color.border_gray_dark)}>
              <img src={'/images/residents/invalid-device.svg'} alt='DFreeデバイス' />-
            </div>
            {/* gridで3個目の要素で折り返すようにしているので、要素がない場合もdomに配置しておく（DeviceInfoContainer参照） */}
            <div></div>
            <div></div>
          </>
        )}
      </div>
    );
  },
);

export default DfreeGraphListItemHeader;
